/**
 * GraphQL fragments for Audit primitive
 *
 * Complete field coverage aligned with GqlAudit (audit_model.rs)
 * Supports: 11 queries + 8 mutations
 */

import { gql } from '@apollo/client';

/**
 * Core Audit fields fragment - All fields from GqlAudit
 */
export const AUDIT_FIELDS = gql`
  fragment AuditFields on AuditQL {
    # 1. Identity Fields (3 — tenantId skipped per DRR-124)
    id
    auditName
    auditCode

    # 2. Compliance Framework Fields (9)
    frameworkType
    compliancePeriodStart
    compliancePeriodEnd
    regulatoryQuestion
    eventIds
    eventCount
    uniqueEventTypes
    findingsSummary
    violationsDetected

    # 3. User Action Tracking Fields (10)
    userIds
    userActionCount
    primitiveTypesAccessed
    actionTypes
    navigationPaths
    timeSpentSeconds
    accessPatterns
    featuresUsed
    featuresUnused
    adoptionRatePercent

    # 4. Regulatory Requirements Fields (7)
    retentionYears
    retentionExpiresAt
    immutabilityRequired
    signatureRequired
    dataLineage
    regulatoryFrameworks
    complianceStatus

    # 5. Report Generation Fields (6)
    reportFormat
    reportTemplateId
    reportS3Url
    generatedByUserId
    approvedByUserId
    approvalRequired

    # 6. Aggregation Metrics Fields (3)
    uniqueActors
    uniqueEntities
    uniqueWorkflows

    # 7. AI Generation & Chaos Integration Fields (5)
    aiGenerated
    aiConfidence
    chaosExtractionId
    approvalWorkflowId
    previousAuditId

    # 8. Generic Subgraph References (1)
    subgraphReferences

    # 9. Federated Validation Metadata (1)
    lastValidationTimestamp

    # 10. Lifecycle & Audit Fields (5)
    createdAt
    createdByUserId
    updatedAt
    updatedBy
    version

    # 11. Append-Only Architecture Fields (3)
    parentId
    originalId
    isActive
  }
`;

/**
 * Audit list result fragment for paginated queries
 */
export const AUDIT_LIST_RESULT_FIELDS = gql`
  ${AUDIT_FIELDS}
  fragment AuditListResultFields on AuditListResultQL {
    audits {
      ...AuditFields
    }
    nextToken
  }
`;

/**
 * Audit report fragment for generated reports
 */
export const AUDIT_REPORT_FIELDS = gql`
  fragment AuditReportFields on AuditReportQL {
    reportId
    auditId
    reportUrl
    generatedAt
    reportFormat
    sizeBytes
  }
`;

/**
 * Subgraph validation result fragment for federated validation
 */
export const SUBGRAPH_VALIDATION_RESULT_FIELDS = gql`
  fragment SubgraphValidationResultFields on SubgraphValidationResultQL {
    subgraphName
    isValid
    validationTimestamp
    errorMessages
    warnings
  }
`;

/**
 * Audit statistics fragment for aggregated metrics
 */
export const AUDIT_STATISTICS_FIELDS = gql`
  fragment AuditStatisticsFields on AuditStatisticsQL {
    totalAudits
    compliantCount
    violationsCount
    underReviewCount
    aiGeneratedCount
    averageViolationCount
  }
`;
